import { memo, useEffect, useMemo, useState } from "react";
import AppsContent from "@lib/components/AppComponents/AppsContainer/AppsContent";
import { useQueryClient } from "@tanstack/react-query";
import AppTable from "@lib/components/MiniComponents/Table/AppTable";
import getColumns from "./columns";
import AppsHeader from "@lib/components/AppComponents/AppsContainer/AppsHeader";
import AppTableContentHeader from "@lib/components/MiniComponents/Table/AppTableContentHeader";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  PaginationType,
  TableHeaderFilter,
  UseQueryProps,
} from "@lib/types/Api";
import { Wallet, WalletAddress } from "@lib/types/Billing";
import { toast } from "react-toastify";
import { WalletAddressModal } from "@lib/components/PageComponents/Wallet";
import { type TableProps } from "antd";
import { handleTableChange } from "@lib/helpers/UrlHelper";
import { useParamsManager } from "@lib/hooks/useParamsManager";
import { useIntl } from "react-intl";
import WalletItemDetail from "./WalletItemDetail";
import { TbListDetails } from "react-icons/tb";
import { IoMdClose } from "react-icons/io";
import { useWalletAddressQueries } from "@lib/apiQueries";
import { AppContext } from "@lib/types";
import { ActionButton } from "@lib/components/MiniComponents/Buttons";
import { TableColumnConfig } from "@lib/types/General";

type Props = {
  walletsQuery: UseQueryProps<PaginationType<Wallet>>;
  context: AppContext;
  detailContext?: AppContext;
  customerId?: string;
  pageIsActive?: boolean;
  columnsConfig?: TableColumnConfig<WalletAddress>;
};

const WalletAddressList = ({
  walletsQuery,
  context,
  detailContext,
  customerId,
  pageIsActive = true,
  columnsConfig,
}: Props) => {
  const { walletId } = useParams();
  const { messages } = useIntl();
  const navigate = useNavigate();
  const { state } = useLocation();

  const filter = []
    .concat(walletId ? [`walletId||$eq||${walletId}`] : [])
    .concat(...(state?.filter || []))
    .concat(...(customerId ? [`customerId||$eq||${customerId}`] : []));

  const { params, setParams, handleRemoveFilters, onPageChange } =
    useParamsManager({
      page: 1,
      limit: 100,
      sort: ["updatedAt,DESC"],
      join: ["customer", "customerUser", "wallet"],
      filter: filter,
    });

  useEffect(() => {
    setParams({
      filter: walletId ? [`walletId||$eq||${walletId}`] : [],
    });
  }, [walletId]);

  const {
    walletAddressQuery,
    deleteWalletAddressMutation,
    addScanWalletAddressMutation,
  } = useWalletAddressQueries({
    params,
    messages,
    queryKey: ["wallet-address", params],
    enabled: !!params && pageIsActive,
  });
  const { data, isLoading, isFetching } = walletAddressQuery;
  const apiData = data?.data?.data;

  useEffect(() => {
    if (
      deleteWalletAddressMutation.isSuccess ||
      addScanWalletAddressMutation.isSuccess
    ) {
      setScanning(true);
    }
  }, [
    deleteWalletAddressMutation.isSuccess,
    addScanWalletAddressMutation.isSuccess,
  ]);

  const queryClient = useQueryClient();
  const [scanning, setScanning] = useState(false);
  const [openModal, setModalOpen] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<WalletAddress>();

  useEffect(() => {
    if (scanning) {
      toast.promise(
        new Promise((resolve) => {
          setTimeout(() => {
            queryClient.invalidateQueries({
              queryKey: ["billing-wallet-address"],
            });
            queryClient.removeQueries({
              queryKey: ["customer-wallet-address"],
            });
            queryClient.removeQueries({
              queryKey: ["scanned-transaction"],
            });
            setScanning(false);
            return resolve(true);
          }, 5000);
        }),
        {
          pending: messages["scanRequest.pending"] as string,
          success: messages["scanRequest.success"] as string,
          error: messages["scanRequest.error"] as string,
        },
      );
    }
  }, [scanning]);

  useEffect(() => {
    if (!openModal) setSelectedAddress(null);
  }, [openModal]);

  const toggle = (status?: boolean) => {
    if (typeof status !== "undefined") setModalOpen(status);
    else setModalOpen(!openModal);
  };

  const handleSelectAddress = (walletAddress: WalletAddress) => {
    setSelectedAddress(walletAddress);
    toggle(true);
  };

  const onChange: TableProps<WalletAddress>["onChange"] = (
    pagination,
    filters,
    sorter,
  ) => {
    handleTableChange({ pagination, filters, sorter, params, setParams });
  };

  const columns = useMemo(
    () =>
      getColumns({
        columnsConfig,
        context,
        detailContext,
        apiData,
        deleteQuery: deleteWalletAddressMutation,
        openModal,
        handleSelectAddress,
        params,
        messages,
        navigate,
        addScanQuery: addScanWalletAddressMutation,
      }),
    [
      apiData,
      deleteWalletAddressMutation,
      openModal,
      handleSelectAddress,
      params,
      addScanWalletAddressMutation,
      columnsConfig,
    ],
  );

  const hiddenFilters = useMemo(() => {
    if (!!detailContext) {
      return ["walletId", "customerId", "customerUserId"];
    }
    if (context === AppContext.CUSTOMER_LEAD) {
      return ["walletId", "customerUserId"];
    }
    if (context === AppContext.CUSTOMER) {
      return ["walletId", "customerId"];
    }
    return [];
  }, [context, detailContext]);

  const filters = useMemo(() => {
    if (!params?.filter) return [];

    return params?.filter
      .map((f) => ({
        key: f.split("||")[0],
        title: columns.find((c) => c?.key === f.split("||")[0])?.title || "",
        operator: f.split("||")[1],
        value: f.split("||")[2],
      }))
      .filter((f) => !hiddenFilters.includes(f.key)) as TableHeaderFilter[];
  }, [params, columns]);

  return (
    <>
      <AppsHeader>
        <AppTableContentHeader
          showInfo
          page={params?.page || 1}
          perPage={params?.limit || 100}
          onPageChange={onPageChange}
          query={walletAddressQuery}
          filters={filters}
          handleRemoveFilters={handleRemoveFilters}
          plusProps={{
            onClick: () => toggle(true),
            disabled: isLoading,
            loading: isLoading,
          }}
        />
      </AppsHeader>
      <AppsContent>
        <AppTable
          dataSource={apiData}
          columns={columns}
          onChange={onChange}
          rowKey={"id"}
          loading={isLoading || isFetching || scanning}
          minusHeight={20}
          expandable={{
            // showExpandColumn: false,
            columnTitle: "#",
            columnWidth: "50px",
            fixed: "right",
            expandIcon: ({ expanded, record, onExpand }) => (
              <ActionButton
                type={expanded ? "close" : "detail"}
                icon={expanded ? <IoMdClose /> : <TbListDetails />}
                color={expanded ? "red" : "green"}
                onClick={(e) => onExpand(record, e)}
                noLabel
              />
            ),
            expandedRowRender: (record) => <WalletItemDetail item={record} />,
            onExpand(expanded, record) {
              try {
                const find = document.querySelector(
                  `[data-row-key="${record.id}"]`,
                );
                if (find) {
                  find.setAttribute(
                    "data-row-expanded",
                    expanded ? "true" : "false",
                  );
                }
              } catch (error) {}
            },
          }}
        />

        <WalletAddressModal.BillingWalletAddressModal
          walletsQuery={walletsQuery}
          openModal={openModal}
          toggleModal={toggle}
          walletAddress={selectedAddress}
          customerId={customerId}
        />
      </AppsContent>
    </>
  );
};

export default memo(WalletAddressList);
